import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Smartphone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ZoomableImage } from "@/components/ZoomableImage";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const Line = ({ children, delay, className }) => (
  <span className="line-mask">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 8]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-28">
      <div className="pointer-events-none absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-accent/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-20 bottom-0 h-[380px] w-[380px] rounded-full bg-secondary/10 blur-3xl" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 lg:grid-cols-12 lg:gap-6 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/5 px-4 py-1.5 text-sm font-medium text-secondary">
            <span className="h-2 w-2 rounded-full bg-accent" /> {t.hero.tag}
          </motion.div>

          <h1 className="font-heading text-5xl font-bold leading-[0.95] tracking-tighter text-primary sm:text-6xl lg:text-7xl">
            <Line delay={0.25}>{t.hero.l1}</Line>
            <Line delay={0.38} className="text-secondary">{t.hero.l2}</Line>
            <Line delay={0.51} className="text-accent">{t.hero.l3}</Line>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground">
            {t.hero.sub}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollTo("products")} data-testid="hero-cta-btn"
              className="group flex items-center gap-2 rounded-full bg-primary px-7 py-4 font-semibold text-primary-foreground transition-transform hover:bg-secondary active:scale-95">
              {t.hero.cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: yImg, rotate }} className="relative mx-auto max-w-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-3xl border border-white/60 shadow-[0_40px_80px_-20px_rgba(10,37,64,0.4)]">
              <ZoomableImage src="https://customer-assets-39nsmqrw.emergentagent.net/job_nfc-pharmacy-connect/artifacts/9n479pl4_FA5C5192-35A5-4111-BA1D-FD395D34578D.PNG"
                alt="aponiqo NFC pods" testId="hero-image" className="aspect-[4/3] w-full object-cover object-center" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.6 }}
              className="glass absolute -left-6 bottom-10 flex items-center gap-3 rounded-2xl p-4 shadow-xl"
              data-testid="hero-badge">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-accent text-white">
                <Smartphone className="h-5 w-5" />
              </span>
              <p className="font-heading text-xl font-bold leading-none">
                <span className="text-primary">Tik</span>
                <span className="text-secondary">, klik</span>
                <span className="text-accent">, groei</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
